import React, { useEffect, useState, useCallback } from 'react';
import { patientHistoryService, type PersonalPathological } from '../services/patientHistoryService';
import EmptyState from '../../../components/ui/EmptyState';
import ConfirmModal from '../../../components/common/ConfirmModal';
import Spinner from '../../../components/ui/Spinner';

interface PersonalPathologicalHistoryProps {
  patientId: number;
}

interface FormData {
  condition_name: string;
  icd10_code: string;
  diagnosis_year: number | '';
  status: 'active' | 'resolved' | 'chronic' | '';
  severity: 'mild' | 'moderate' | 'severe' | '';
  current_treatment: string;
  notes: string;
}

const emptyForm: FormData = {
  condition_name: '',
  icd10_code: '',
  diagnosis_year: '',
  status: '',
  severity: '',
  current_treatment: '',
  notes: '',
};

const STATUS_LABELS: Record<string, string> = {
  active: 'Activo',
  resolved: 'Resuelto',
  chronic: 'Crónico',
};

const SEVERITY_LABELS: Record<string, string> = {
  mild: 'Leve',
  moderate: 'Moderado',
  severe: 'Severo',
};

const PersonalPathologicalHistory: React.FC<PersonalPathologicalHistoryProps> = ({ patientId }) => {
  const [items, setItems] = useState<PersonalPathological[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await patientHistoryService.getPersonalPathologicalHistory(patientId);
      setItems(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar antecedentes patológicos');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    void fetchItems();
  }, [fetchItems]);

  const handleChange = (field: keyof FormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.condition_name.trim()) {
      setError('El nombre de la condición es requerido');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const payload = {
        condition_name: formData.condition_name.trim(),
        icd10_code: formData.icd10_code.trim() || null,
        diagnosis_year: formData.diagnosis_year === '' ? null : Number(formData.diagnosis_year),
        status: formData.status || null,
        severity: formData.severity || null,
        current_treatment: formData.current_treatment.trim() || null,
        notes: formData.notes.trim() || null,
      };

      if (editingId !== null) {
        await patientHistoryService.updatePersonalPathological(patientId, editingId, payload);
      } else {
        await patientHistoryService.createPersonalPathological(patientId, payload);
      }

      setFormData(emptyForm);
      setShowForm(false);
      setEditingId(null);
      void fetchItems();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al guardar antecedente');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item: PersonalPathological) => {
    setEditingId(item.id!);
    setFormData({
      condition_name: item.condition_name,
      icd10_code: item.icd10_code ?? '',
      diagnosis_year: item.diagnosis_year ?? '',
      status: item.status ?? '',
      severity: item.severity ?? '',
      current_treatment: item.current_treatment ?? '',
      notes: item.notes ?? '',
    });
    setShowForm(true);
  };

  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const requestDelete = (historyId: number) => setPendingDeleteId(historyId);

  const cancelDelete = () => {
    if (deleting) return;
    setPendingDeleteId(null);
  };

  const confirmDelete = async () => {
    if (pendingDeleteId == null) return;
    setDeleting(true);
    const id = pendingDeleteId;
    setPendingDeleteId(null);
    try {
      await patientHistoryService.deletePersonalPathological(patientId, id);
      void fetchItems();
    } catch (err) {
      console.error('Error deleting:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = () => {
    setFormData(emptyForm);
    setShowForm(false);
    setEditingId(null);
    setError(null);
  };

  if (loading) {
    return <Spinner variant="full-page" text="Cargando antecedentes patológicos..." />;
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">
          <i className="fa fa-file-medical"></i> Antecedentes Patológicos Personales
        </h3>
        <div className="card-tools">
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              setShowForm(!showForm);
              setEditingId(null);
              setFormData(emptyForm);
              setError(null);
            }}
          >
            <i className={`fa ${showForm ? 'fa-times' : 'fa-plus'}`}></i>
            {showForm ? ' Cancelar' : ' Agregar'}
          </button>
        </div>
      </div>
      <div className="card-body">
        {error && (
          <div className="alert alert-danger alert-sm mb-3">
            <i className="fa fa-exclamation-circle me-2"></i>
            {error}
          </div>
        )}

        {/* Form */}
        {showForm && (
          <div className="card card-info mb-4" style={{ backgroundColor: '#f9f9f9' }}>
            <div className="card-header">
              <h4 className="card-title">
                {editingId !== null ? 'Editar Antecedente' : 'Nuevo Antecedente'}
              </h4>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  {/* Condition Name */}
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Nombre de la Condición *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.condition_name}
                        onChange={(e) => handleChange('condition_name', e.target.value)}
                        placeholder="Ej: Diabetes Mellitus"
                        required
                      />
                    </div>
                  </div>

                  {/* ICD-10 Code */}
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Código ICD-10</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.icd10_code}
                        onChange={(e) => handleChange('icd10_code', e.target.value)}
                        placeholder="Ej: E11.9"
                      />
                    </div>
                  </div>

                  {/* Diagnosis Year */}
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Año de Diagnóstico</label>
                      <input
                        type="number"
                        className="form-control"
                        value={formData.diagnosis_year}
                        onChange={(e) => handleChange('diagnosis_year', e.target.value)}
                        min={1900}
                        max={new Date().getFullYear()}
                        placeholder="Ej: 2020"
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  {/* Status */}
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Estado</label>
                      <select
                        className="form-control"
                        value={formData.status}
                        onChange={(e) => handleChange('status', e.target.value)}
                      >
                        <option value="">Seleccionar...</option>
                        <option value="active">Activo</option>
                        <option value="resolved">Resuelto</option>
                        <option value="chronic">Crónico</option>
                      </select>
                    </div>
                  </div>

                  {/* Severity */}
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Severidad</label>
                      <select
                        className="form-control"
                        value={formData.severity}
                        onChange={(e) => handleChange('severity', e.target.value)}
                      >
                        <option value="">Seleccionar...</option>
                        <option value="mild">Leve</option>
                        <option value="moderate">Moderado</option>
                        <option value="severe">Severo</option>
                      </select>
                    </div>
                  </div>

                  {/* Current Treatment */}
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Tratamiento Actual</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.current_treatment}
                        onChange={(e) => handleChange('current_treatment', e.target.value)}
                        placeholder="Ej: Metformina 500mg"
                      />
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group">
                      <label>Notas</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={formData.notes}
                        onChange={(e) => handleChange('notes', e.target.value)}
                        placeholder="Observaciones adicionales..."
                      />
                    </div>
                  </div>
                </div>

                <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                  {saving ? (
                    <Spinner size="sm" text="Guardando..." />
                  ) : (
                    <><i className="fa fa-save"></i> {editingId !== null ? 'Actualizar' : 'Guardar'}</>
                  )}
                </button>
                <button type="button" className="btn btn-outline-secondary btn-sm ms-2" onClick={handleCancel}>
                  Cancelar
                </button>
              </form>
            </div>
          </div>
        )}

        {/* List */}
        {items.length === 0 && !showForm ? (
          <EmptyState
            icon="fa-file-lines"
            title="No tiene antecedentes patológicos registrados"
            className="py-4"
          />
        ) : (
          <div className="table-responsive">
            <table className="table table-striped table-hover">
              <thead>
                <tr>
                  <th>Condición</th>
                  <th>Código ICD-10</th>
                  <th>Año</th>
                  <th>Estado</th>
                  <th>Severidad</th>
                  <th>Tratamiento Actual</th>
                  <th>Notas</th>
                  <th style={{ width: '100px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td><strong>{item.condition_name}</strong></td>
                    <td><code>{item.icd10_code || '-'}</code></td>
                    <td>{item.diagnosis_year || '-'}</td>
                    <td>
                      {item.status && (
                        <span className={`badge ${
                          item.status === 'active' ? 'bg-danger' :
                          item.status === 'resolved' ? 'bg-success' :
                          'bg-warning'
                        }`}>
                          {STATUS_LABELS[item.status]}
                        </span>
                      )}
                    </td>
                    <td>
                      {item.severity && (
                        <span className={`badge ${
                          item.severity === 'mild' ? 'bg-success' :
                          item.severity === 'moderate' ? 'bg-warning' :
                          'bg-danger'
                        }`}>
                          {SEVERITY_LABELS[item.severity]}
                        </span>
                      )}
                    </td>
                    <td><small>{item.current_treatment || '-'}</small></td>
                    <td><small className="text-muted">{item.notes || '-'}</small></td>
                    <td>
                      <button
                        className="btn btn-xs btn-primary me-1"
                        title="Editar"
                        onClick={() => handleEdit(item)}
                      >
                        <i className="fa fa-edit"></i>
                      </button>
                      <button
                        className="btn btn-xs btn-danger"
                        title="Eliminar"
                        onClick={() => requestDelete(item.id!)}
                      >
                        <i className="fa fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        show={pendingDeleteId !== null}
        title="Eliminar antecedente patológico"
        body="¿Está seguro que desea eliminar este antecedente patológico? Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        confirmVariant="danger"
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};

export default PersonalPathologicalHistory;
